// Pure power bidding-zone data for the market-coverage map.
//
// Like coverage.ts / japan.ts this module has NO DOM and NO Leaflet dependency,
// so it can be unit-tested in isolation. It owns:
//   1. the descriptor for each intra-country power bidding zone (label, parent
//      country, KYOS power feed),
//   2. the set of countries that are split into zones on the power map,
//   3. the bundled zone polygons (each tagged with its `zone` code) that the
//      power map draws instead of the single country shape.
//
// Scope: the IT/SE/NO sub-country split tracked in #187 part 2 (#320), plus the
// Danish DK1/DK2 split (#327). It is deliberately SEPARATE from the DE-LU
// `PowerZone` in coverage.ts — that one *groups* whole countries into one zone;
// this one *splits* one country into several. DE-LU is untouched by this module.
//
// Geometry source: ElectricityMaps `geo/world.geojson`
// (github.com/electricitymaps/electricitymaps-contrib, ODbL), filtered to the
// NO/SE/IT/DK bidding zones and simplified offline (coords rounded to 3 dp). Each
// feature carries a `zone` + `country` property so the renderer styles it
// without needing the source attributes at runtime.

import powerBiddingZones from './data/power-bidding-zones.json'

/** Descriptor for one intra-country power bidding zone. */
export interface BiddingZone {
    /** Geographic label shown after the code, e.g. "Norway South-East". */
    name: string
    /** Parent country — used for the tooltip flag and to suppress its country shape. */
    country: string
    /**
     * KYOS power feed surfaced in the tooltip. NOTE: provisional — confirm the
     * exact feed strings against power.kyos.com before this ships.
     */
    feed: string
}

/**
 * Bidding-zone code → descriptor. Codes match the `zone` property baked into the
 * bundled polygons. KYOS publishes a feed for every zone, so all are covered.
 *
 * Italy uses the 6-zone ElectricityMaps model (no separate Calabria/CALA — it is
 * folded into SUD). If power.kyos.com publishes a distinct CALA feed, SUD must be
 * split and a CALA polygon sourced — tracked as a follow-up on #320.
 */
export const BIDDING_ZONES: Readonly<Record<string, BiddingZone>> = {
    // Norway (Nordpool)
    NO1: { name: 'Norway South-East', country: 'Norway', feed: 'Nordpool Power NO1' },
    NO2: { name: 'Norway South', country: 'Norway', feed: 'Nordpool Power NO2' },
    NO3: { name: 'Norway Central', country: 'Norway', feed: 'Nordpool Power NO3' },
    NO4: { name: 'Norway North', country: 'Norway', feed: 'Nordpool Power NO4' },
    NO5: { name: 'Norway West', country: 'Norway', feed: 'Nordpool Power NO5' },
    // Sweden (Nordpool)
    SE1: { name: 'Sweden North', country: 'Sweden', feed: 'Nordpool Power SE1' },
    SE2: { name: 'Sweden North-Central', country: 'Sweden', feed: 'Nordpool Power SE2' },
    SE3: { name: 'Sweden South-Central', country: 'Sweden', feed: 'Nordpool Power SE3' },
    SE4: { name: 'Sweden South', country: 'Sweden', feed: 'Nordpool Power SE4' },
    // Italy (GME/ENTSO-E)
    NORD: { name: 'Italy North', country: 'Italy', feed: 'IT Power NORD' },
    CNOR: { name: 'Italy Centre-North', country: 'Italy', feed: 'IT Power CNOR' },
    CSUD: { name: 'Italy Centre-South', country: 'Italy', feed: 'IT Power CSUD' },
    SUD: { name: 'Italy South', country: 'Italy', feed: 'IT Power SUD' },
    SICI: { name: 'Sicily', country: 'Italy', feed: 'IT Power SICI' },
    SARD: { name: 'Sardinia', country: 'Italy', feed: 'IT Power SARD' },

    // Denmark (#327): DK1 = West (Jutland & Funen), DK2 = East (Zealand).
    // Feeds provisional — confirm the exact strings against power.kyos.com.
    DK1: { name: 'Denmark West', country: 'Denmark', feed: 'Nordpool Power DK1' },
    DK2: { name: 'Denmark East', country: 'Denmark', feed: 'Nordpool Power DK2' },
}

/**
 * Countries whose single country polygon is replaced by their bidding zones on
 * the POWER map. On the gas map these countries behave normally (country-level).
 * Derived from BIDDING_ZONES so it can never drift from the zone list.
 */
export const ZONE_SPLIT_COUNTRIES: ReadonlySet<string> = new Set(
    Object.values(BIDDING_ZONES).map((zone) => zone.country),
)

/**
 * Resolve the descriptor for a zone code (e.g. a polygon's `zone` property).
 * Returns `undefined` for unknown codes — never throws.
 */
export function biddingZoneInfo(code: string): BiddingZone | undefined {
    return BIDDING_ZONES[code]
}

// Bundled (no runtime fetch): the NO/SE/IT/DK bidding-zone polygons.
export { powerBiddingZones }
